import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const whatsappCallbackBody = z.object({
  state: z.string().min(1, 'state is required'),
  code: z.string().optional(),
  access_token: z.string().optional(),
  user_id: z.string().optional(),
}).refine(
  (data) => !!data.access_token || !!data.code,
  { message: 'Missing required parameters: state and either access_token or code' }
);

// =============================================================================
// AUTH ME — GET /api/auth/me
// =============================================================================

export const authMeResponse = z.object({
  user_id: z.string(),
  email: z.string(),
  org_id: z.string().nullable(),
  is_platform_admin: z.boolean(),
  // Present so the MCP server can conversation-scope + harness-gate the wayai-conversation
  // control surface (harness-agents PR 2.7c): session_id = conversation_id for a harness
  // token; is_harness marks the synthetic in-sandbox principal. Optional/back-compat.
  session_id: z.string().optional(),
  is_harness: z.boolean().optional(),
});

export type AuthMeResponse = z.infer<typeof authMeResponse>;

// =============================================================================
// WS TICKET — POST /api/auth/ws-ticket
// =============================================================================

export const wsTicketResponse = z.object({
  ticket: z.string(),
  /** Seconds until the ticket expires if unconsumed. */
  expires_in: z.number(),
});

export type WsTicketResponse = z.infer<typeof wsTicketResponse>;

// =============================================================================
// SESSION CHECK — GET /api/auth/session-check
// =============================================================================

export const sessionCheckResponse = z.object({
  valid: z.literal(true),
});

export type SessionCheckResponse = z.infer<typeof sessionCheckResponse>;

// =============================================================================
// LOGOUT — POST /api/auth/logout
// =============================================================================

export const logoutResponse = z.object({
  success: z.literal(true),
});

export type LogoutResponse = z.infer<typeof logoutResponse>;

// =============================================================================
// MAGIC CODE — POST /api/auth/magic-code/send + /verify (native mobile login)
// =============================================================================

export const magicCodeSendBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
});
export type MagicCodeSendRequest = z.infer<typeof magicCodeSendBody>;

// Uniform response whether or not the email has an account (anti-enumeration).
export const magicCodeSendResponse = z.object({
  success: z.literal(true),
});
export type MagicCodeSendResponse = z.infer<typeof magicCodeSendResponse>;

export const magicCodeVerifyBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
  code: z.string().min(1, 'Code is required').max(12), // WorkOS sends a 6-digit code; bound the input
});
export type MagicCodeVerifyRequest = z.infer<typeof magicCodeVerifyBody>;

// Same token shape the OAuth flow stores — WorkOS User Management access + refresh tokens.
export const magicCodeVerifyResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
});
export type MagicCodeVerifyResponse = z.infer<typeof magicCodeVerifyResponse>;

// =============================================================================
// PASSWORD LOGIN — POST /api/auth/password/login (native login, WorkOS password auth)
// =============================================================================
// Exists because the app stores (Apple 2.1a, Google Play sign-in requirements) demand
// typeable, reusable, non-expiring reviewer credentials — which a single-use magic code
// can never be. Login only: there is deliberately no in-app set/reset flow, so a password
// exists only on accounts explicitly given one in the WorkOS dashboard (the review account).

export const passwordLoginBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
  password: z.string().min(1, 'Password is required').max(128), // bound the payload; WorkOS validates
});
export type PasswordLoginRequest = z.infer<typeof passwordLoginBody>;

// Same token pair as the magic-code + OAuth flows.
export const passwordLoginResponse = magicCodeVerifyResponse;
export type PasswordLoginResponse = z.infer<typeof passwordLoginResponse>;

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type WhatsappCallbackInput = z.infer<typeof whatsappCallbackBody>;
